import React from 'react';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ServicePageLayout } from '../components/ServicePageLayout';
export function HeadlightRestorationPage() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Headlight Restoration',
    provider: {
      '@type': 'LocalBusiness',
      name: "Mike's Pro Detail",
      telephone: '+18183819905'
    },
    areaServed: ['Camarillo', 'Thousand Oaks', 'Ventura County', 'Los Angeles'],
    description: 'Professional headlight restoration service to remove yellowing, oxidation, and fog, improving nighttime visibility and vehicle appearance.'
  };
  const faqs = [{
    question: 'How long does headlight restoration last?',
    answer: 'Because we apply a durable UV sealant or ceramic coating as the final step, our headlight restoration lasts far longer than cheap DIY kits \u2014 typically a year or more, depending on sun exposure and care.'
  }, {
    question: 'Why do headlights turn yellow and cloudy?',
    answer: 'Headlight lenses are made of polycarbonate plastic with a factory UV clear coat. Over time that coating breaks down from sun and heat, causing the plastic underneath to oxidize and turn yellow or foggy.'
  }, {
    question: 'Is headlight restoration worth it vs. replacement?',
    answer: 'Yes. Replacing headlight assemblies can cost hundreds or thousands of dollars. Restoration delivers clear, like-new lenses for a fraction of the cost.'
  }, {
    question: 'Does it improve nighttime visibility?',
    answer: 'Significantly. Clear lenses let your bulbs project light properly, improving your vision at night and in poor weather and keeping you safer on the road.'
  }];
  const breadcrumbs = [{
    name: 'Home',
    item: 'https://www.fulldetailpro.com/'
  }, {
    name: 'Services',
    item: 'https://www.fulldetailpro.com/#services'
  }, {
    name: 'Headlight Restoration',
    item: 'https://www.fulldetailpro.com/services/headlight-restoration'
  }];
  return <ServicePageLayout title="Headlight Restoration Ventura County" subtitle="Improve your nighttime visibility and enhance your car's appearance by permanently removing yellowing, oxidation, and fog from your headlights." metaDescription="Professional headlight restoration in Ventura County \u2014 Camarillo, Oxnard & Thousand Oaks. Remove yellowing & oxidation for clear headlights. Call (818) 381-9905." schema={schema} faqs={faqs} breadcrumbs={breadcrumbs} data-id="element-951" data-mp="1zs0pg2a1vvp">
      <p data-id="element-952" data-mp="3keax5i4y2hqd">
        Are your vehicle's headlights looking cloudy, yellowed, or foggy? Not
        only does this age the appearance of your car significantly, but it also
        poses a serious safety hazard by drastically reducing your nighttime
        visibility. At Mike's Pro Detail, our professional{' '}
        <strong data-id="element-953" data-mp="2wprr799dah1g">Headlight Restoration</strong> service permanently removes heavy
        oxidation and restores crystal-clear clarity to your lenses. We provide
        this essential mobile service throughout Camarillo CA, Thousand Oaks,
        and the greater Ventura County area.
      </p>

      <h2 data-id="element-954" data-mp="1eqttl2hkqhaf">Why Do Headlights Turn Yellow?</h2>
      <p data-id="element-955" data-mp="1es4umujmxaxu">
        Modern vehicle headlights are made of polycarbonate plastic, which is
        lightweight and impact-resistant. However, this plastic is highly
        susceptible to UV degradation from the sun. Manufacturers apply a
        UV-protective clear coat at the factory, but over time, this coating
        breaks down due to sun exposure, heat from the bulbs, and harsh cleaning
        chemicals. Once the coating fails, the plastic oxidizes rapidly,
        resulting in that cloudy, yellow appearance.
      </p>

      <h2 data-id="element-956" data-mp="m2o7abavgk8h">Our Professional Restoration Process</h2>
      <p data-id="element-957" data-mp="m3z7k153z9uu">
        Don't be fooled by cheap DIY kits or "wipe-on" fixes that only last a
        few weeks. We perform a true, multi-step mechanical restoration to
        permanently fix the issue:
      </p>

      <ul className="space-y-4 my-6 list-none pl-0" data-id="element-958" data-mp="c51nbaepu2hn">
        <li className="flex items-start" data-id="element-959" data-mp="2c67x1zifuq80">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-960" data-mp="2c79jt18th15k" />
          <span data-id="element-961" data-mp="2h642m36sappo">
            <strong data-id="element-962" data-mp="2h4j8ufsanasv">Taping & Protection:</strong> We carefully tape off the
            surrounding paint and trim to ensure no damage occurs during the
            sanding process.
          </span>
        </li>
        <li className="flex items-start" data-id="element-963" data-mp="27eghsl4qg6x6">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-964" data-mp="1tg9tnwpl5lap" />
          <span data-id="element-965" data-mp="1ticqp0p5oeox">
            <strong data-id="element-966" data-mp="1yfcwrbm09hg1">Wet Sanding:</strong> We use progressive grits of
            specialized sandpaper to completely strip away the failed factory
            clear coat and the oxidized plastic layer.
          </span>
        </li>
        <li className="flex items-start" data-id="element-967" data-mp="1ogdlrmdrp9pv">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-968" data-mp="1ohf8io45bknf" />
          <span data-id="element-969" data-mp="3dx6ckzhvv92f">
            <strong data-id="element-970" data-mp="3dwmrj9wxs4k6">Machine Compounding:</strong> Using a machine polisher and
            heavy cutting compound, we remove the fine sanding marks and begin
            restoring clarity.
          </span>
        </li>
        <li className="flex items-start" data-id="element-971" data-mp="175rw7ysts9am">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-972" data-mp="1299sn298egzv" />
          <span data-id="element-973" data-mp="126x5maom9gfy">
            <strong data-id="element-974" data-mp="1gxe69rtfg2bs">Machine Polishing:</strong> A refining polish is used to
            bring the polycarbonate lens back to a flawless, glass-like
            transparency.
          </span>
        </li>
        <li className="flex items-start" data-id="element-975" data-mp="1pxt2hm7xj1u2">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-976" data-mp="1pwrfqkhjwqwi" />
          <span data-id="element-977" data-mp="1lxikfb6v4kyu">
            <strong data-id="element-978" data-mp="1lvz5bnwy0fyn">UV Protection Application:</strong> This is the most
            critical step. We apply a durable ceramic coating or specialized UV
            sealant to protect the bare plastic and prevent it from yellowing
            again.
          </span>
        </li>
      </ul>

      <h2 data-id="element-979" data-mp="5jk0c3obqb9s">Safety and Aesthetics</h2>
      <p data-id="element-980" data-mp="5klewi4jtfw9">
        Restoring your headlights is one of the most cost-effective ways to
        instantly improve the look of your vehicle. More importantly, clear
        headlights allow your bulbs to project light properly, significantly
        improving your vision at night and during inclement weather, keeping you
        and your family safe on the road.
      </p>

      <h2 data-id="element-981" data-mp="29lkdkuhz2me5">Convenient Mobile Service</h2>
      <p data-id="element-982" data-mp="29oom242h04am">
        Replacing headlight assemblies can cost hundreds or even thousands of
        dollars. Our restoration service is a fraction of the cost and yields
        incredible results. If you are searching for "Mobile Detailing Ventura
        County" or "Car Detailing Camarillo" to fix your cloudy headlights,
        Mike's Pro Detail will come directly to your home or workplace to
        perform the restoration.
      </p>

      <h2 data-id="element-983" data-mp="2xc68dk2yua67">Related Detailing Services</h2>
      <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6" data-id="element-984" data-mp="2xb4yvk1llvql">
        <Link to="/services/full-car-detail" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-985" data-mp="32cc6nj3qqux9">
          Full Car Detail &rarr;
        </Link>
        <Link to="/services/exterior-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-986" data-mp="3cf7mw8g1u2wc">
          Exterior Detailing &rarr;
        </Link>
        <Link to="/services/engine-bay-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-987" data-mp="33g2pj5g84flr">
          Engine Bay Detailing &rarr;
        </Link>
        <Link to="/services/mobile-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-988" data-mp="3qdxljsiioamt">
          Mobile Detailing &rarr;
        </Link>
      </div>
    </ServicePageLayout>;
}